import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CreditCard, BookOpen, Zap, Star } from 'lucide-react';
import { CTAButton } from './Shared';
const heroImage = {
  src: 'https://cdn.magicpatterns.com/uploads/pFKmjLSbXP2aBhvZt9XSt4/gfdgdgd.png',
  alt: 'Santuario Digital: Mamá Sigue Aquí'
};
export const Hero = () => {
  return <div className="relative overflow-hidden pb-8 md:pt-20 md:pb-12 bg-transparent pt-[10px]" data-id="element-112">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-pinkLight/10 rounded-full blur-[140px] -z-10 pointer-events-none" data-id="element-113"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-114">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="bg-transparent space-y-[0px]" data-id="element-115">
          <div className="inline-flex items-center justify-center bg-white/80 backdrop-blur-sm border border-brand-pink/30 px-4 py-1.5 rounded-full mb-6 gap-2 shadow-sm" data-id="element-116">
            <span className="text-sm" data-id="element-117">🌿</span>
            <span className="font-serif font-semibold tracking-wider text-xs md:text-sm text-brand-pinkDark" data-id="element-118">
              LUMEN AKASHA™
            </span>
          </div>

          <div className="relative inline-block mb-4 max-w-4xl mx-auto" data-id="element-119">
            {/* Ambient glow behind headline */}
            <div className="absolute inset-0 -z-10 blur-3xl opacity-60 pointer-events-none" data-id="element-120">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[140%] bg-gradient-to-r from-brand-pink/20 via-brand-gold/10 to-brand-pinkDark/20 rounded-full" data-id="element-121" />
            </div>

            {/* Sparkles */}
            <span className="sparkle absolute -top-2 left-[8%] text-brand-gold text-sm md:text-xl" style={{
            animationDelay: '0s'
          }} data-id="element-122">
              ✦
            </span>
            <span className="sparkle absolute top-1 right-[6%] text-brand-pinkLight text-xs md:text-lg" style={{
            animationDelay: '0.8s'
          }} data-id="element-123">
              ✧
            </span>
            <span className="sparkle absolute -bottom-1 left-[20%] text-brand-goldLight text-xs md:text-base" style={{
            animationDelay: '1.6s'
          }} data-id="element-124">
              ✦
            </span>
            <span className="sparkle absolute bottom-2 right-[18%] text-brand-pink text-sm md:text-lg" style={{
            animationDelay: '2.2s'
          }} data-id="element-125">
              ✧
            </span>

            <h1 className="sm:text-4xl md:text-6xl lg:text-7xl text-[28px] font-[700] leading-tight tracking-tight" style={{
            fontFamily: "'Cinzel', serif"
          }} data-id="element-126">
              <span className="headline-shine" data-id="element-127">
                Descubrí una nueva forma de sentir
              </span>
              <br data-id="element-128" />
              <span className="phrase-glow font-[800]" data-id="element-129">el amor de mamá.</span>
            </h1>
          </div>

          {/* Hero Image */}
          <div className="relative z-10 w-full max-w-[320px] sm:max-w-md md:max-w-lg lg:max-w-2xl mx-auto mb-2 flex justify-center" data-id="element-130">
            <div className="relative overflow-hidden rounded-2xl bg-transparent w-full mx-auto" style={{
            aspectRatio: '4/3',
            maxHeight: '500px'
          }} data-id="element-131">
              <motion.img src={heroImage.src} alt={heroImage.alt} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} className="absolute inset-0 w-full h-full object-contain mx-auto drop-shadow-2xl" draggable={false} data-id="element-132" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-8 mt-8 space-y-3 px-2" data-id="element-133">
            <p className="md:text-xl font-bold text-brand-pink font-serif text-[13px] leading-relaxed" data-id="element-134">
              Santuario Digital: Mamá Sigue Aquí — Acompañamiento espiritual
              completo
            </p>
            <p className="md:text-base text-brand-text/80 font-sans text-[12px]" data-id="element-135">
              El sistema de sanación espiritual para quienes perdieron
              físicamente a su madre y necesitan paz, contención y conexión
              interior.
            </p>
            <p className="md:text-sm text-brand-text/70 font-sans text-[11px] italic" data-id="element-136">
              Guía espiritual + escritura sanadora + ritual de 7 días + frases
              de contención listos para usar.
            </p>
          </div>

          <div className="flex flex-nowrap items-center justify-center gap-1.5 md:gap-3 mb-12 text-xs md:text-sm font-medium px-2" data-id="element-137">
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-pink/25 whitespace-nowrap shadow-sm text-[9px] md:text-xs" data-id="element-138">
              <BookOpen className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-pink shrink-0" data-id="element-139" />
              100% PDF
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-pink/25 whitespace-nowrap shadow-sm text-[9px] md:text-xs" data-id="element-140">
              <Zap className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-pink shrink-0" data-id="element-141" />
              Acceso en 5 min
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-pink/25 whitespace-nowrap shadow-sm text-[9px] md:text-xs" data-id="element-142">
              <span className="mr-1 md:mr-1.5 text-brand-pink shrink-0" data-id="element-143">
                🌿
              </span>
              Para cualquier momento del duelo
            </div>
          </div>

          <div className="flex flex-col items-center space-y-6" data-id="element-144">
            <CTAButton variant="pink" className="text-base md:text-xl px-6 md:px-10 py-4 md:py-5 cta-shimmer relative overflow-hidden" data-id="element-145">
              <span className="relative z-10" data-id="element-146">
                🌿 → QUIERO MI ACOMPAÑAMIENTO AHORA
              </span>
            </CTAButton>

            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-xs md:text-sm text-brand-muted" data-id="element-147">
              <div className="flex items-center" data-id="element-148">
                <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-pink" data-id="element-149" />{' '}
                Garantía 7 días
              </div>
              <div className="flex items-center" data-id="element-150">📥 Descarga inmediata</div>
              <div className="flex items-center" data-id="element-151">
                🖨️ Listo para leer, imprimir o usar en tablet
              </div>
              <div className="flex items-center" data-id="element-152">
                <Lock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-pink" data-id="element-153" />{' '}
                Pago seguro
              </div>
              <div className="flex items-center" data-id="element-154">
                <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-pink" data-id="element-155" />{' '}
                Mercado Pago
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Social Proof Bar */}
      <div className="mt-12 border-y border-brand-pink/15 bg-white/60 py-4 backdrop-blur-sm" data-id="element-156">
        <div className="md:hidden flex flex-wrap justify-center gap-x-5 gap-y-2 px-4 text-sm" data-id="element-157">
          <div className="flex items-center" data-id="element-158">
            <span className="font-bold text-brand-pink mr-1.5" data-id="element-159">+300</span>
            personas acompañadas
          </div>
          <div className="flex items-center" data-id="element-160">
            <span className="font-bold text-brand-pink mr-1.5" data-id="element-161">1</span>Tomo
            principal
          </div>
          <div className="flex items-center" data-id="element-162">
            <span className="font-bold text-brand-pink mr-1.5" data-id="element-163">3</span>Bonus
            incluidos
          </div>
          <div className="flex items-center" data-id="element-164">
            <Star className="w-3.5 h-3.5 text-brand-gold fill-brand-gold mr-1" data-id="element-165" />
            4.9/5
          </div>
        </div>

        <div className="hidden md:flex flex-row items-center gap-6 justify-center whitespace-nowrap px-4 text-sm" data-id="element-166">
          <div className="flex items-center" data-id="element-167">
            <span className="font-bold text-brand-pink mr-2" data-id="element-168">+300</span>personas
            acompañadas
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-pink/50 shrink-0" data-id="element-169"></div>
          <div className="flex items-center" data-id="element-170">
            <span className="font-bold text-brand-pink mr-2" data-id="element-171">1</span>Tomo
            principal
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-pink/50 shrink-0" data-id="element-172"></div>
          <div className="flex items-center" data-id="element-173">
            <span className="font-bold text-brand-pink mr-2" data-id="element-174">3</span>Bonus
            incluidos
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-pink/50 shrink-0" data-id="element-175"></div>
          <div className="flex items-center" data-id="element-176">
            <Star className="w-4 h-4 text-brand-gold fill-brand-gold mr-1" data-id="element-177" />
            4.9/5 calificación
          </div>
        </div>
      </div>
    </div>;
};